/* eslint-disable no-undef */
import React from 'react';
import PropTypes from 'prop-types';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import { isAudioMuteButtonDisabled } from 'store/modules/toolbox/functions';
import { getAudioSettingsVisibility } from 'store/modules/settings/functions';
import { connect } from 'store/modules/base/redux/functions';
import { translate } from 'store/modules/base/i18n/functions';
import { isLocalTrackMuted } from 'store/modules/base/tracks/functions';
import { AUDIO_MUTE_BUTTON_ENABLED } from 'store/modules/base/flags/constants';
import { getFeatureFlag } from 'store/modules/base/flags/functions';
import { MEDIA_TYPE } from 'store/modules/base/media/constants';
import AbstractButton from './AbstractButton';
import { JoinAudio, LeaveAudio } from 'assets/images';
import { playSound } from 'store/modules/base/sounds/actions';
import {
  JOIN_AUDIO_SOUND_ID,
  LEAVE_AUDIO_SOUND_ID
} from 'store/modules/chat/constants';
import {
  ACTION_SHORTCUT_TRIGGERED,
  AUDIO_MUTE,
  createShortcutEvent
} from 'store/modules/analytics/AnalyticsEvents';
import { sendAnalytics } from 'store/modules/analytics/functions';
import {
  registerShortcut,
  unregisterShortcut
} from 'store/modules/keyboard-shortcuts/actions';

/**
 * Button used for audio & audio settings.
 *
 * @returns {ReactElement}
 */
class JoinAudioButton extends AbstractButton {
  label = 'toolbar.leaveAudio';
  toggledLabel = 'toolbar.joinAudio';
  type = 'green';
  icon = LeaveAudio;
  toggledIcon = JoinAudio;
  tooltip = 'touch/click this to start';
  /**
   * Initializes a new {@code AudioSettingsButton} instance.
   *
   * @inheritdoc
   */
  constructor(props) {
    super(props);

    this._onEscClick = this._onEscClick.bind(this);
    this._onClick = this._onClick.bind(this);
    // Bind event handlers so they are only bound once per instance.
    this._onKeyboardShortcut = this._onKeyboardShortcut.bind(this);
  }

  /**
   * Click handler for the more actions entries.
   *
   * @param {KeyboardEvent} event - Esc key click to close the popup.
   * @returns {void}
   */
  _onEscClick(event) {
    if (event.key === 'Escape' && this.props.isOpen) {
      event.preventDefault();
      event.stopPropagation();

      this._onClick();
    }
  }

  /**
   * Click handler for the more actions entries.
   *
   * @returns {void}
   */
  // _onClick(muted) {
  //   this.props.onPress(muted);
  // }

  /**
   * Registers the keyboard shortcut that toggles the audio muting.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentDidMount() {
    this.props.dispatch(
      registerShortcut({
        character: 'J',
        helpDescription: 'keyboardShortcuts.joinLeaveAudio',
        handler: this._onKeyboardShortcut
      })
    );
    this.props.dispatch(
      registerShortcut({
        character: 'L',
        helpDescription: 'keyboardShortcuts.joinLeaveAudio',
        handler: this._onKeyboardShortcut
      })
    );
  }

  /**
   * Unregisters the keyboard shortcut that toggles the audio muting.
   *
   * @inheritdoc
   * @returns {void}
   */
  componentWillUnmount() {
    this.props.dispatch(unregisterShortcut('J'));
    this.props.dispatch(unregisterShortcut('L'));
  }

  /**
   * Indicates if audio is currently muted or not.
   *
   * @override
   * @protected
   * @returns {boolean}
   */
  _isAudioMuted() {
    return this.props._isVideoOnly;
  }

  /**
   * Creates an analytics keyboard shortcut event and dispatches an action to
   * toggle the audio muting.
   *
   * @private
   * @returns {void}
   */
  _onKeyboardShortcut() {
    // Ignore keyboard shortcuts if the audio button is disabled.
    if (this._isDisabled()) {
      return;
    }

    sendAnalytics(
      createShortcutEvent(AUDIO_MUTE, ACTION_SHORTCUT_TRIGGERED, {
        enable: !this._isAudioMuted()
      })
    );

    AbstractButton.prototype._onClick.call(this);
  }

  /**
   * Changes the muted state.
   *
   * @param {boolean} audioMuted - Whether audio should be muted or not.
   * @protected
   * @returns {void}
   */
  _setAudioMuted(audioMuted) {
    //@ts-ignore
    APP.conference.handleJoinLeaveAudio(audioMuted ? 'off' : 'on');
    this.props.dispatch(
      playSound(audioMuted ? LEAVE_AUDIO_SOUND_ID : JOIN_AUDIO_SOUND_ID)
    );
    if (this.props.onPress) this.props.onPress(audioMuted);
  }

  /**
   * Return a boolean value indicating if this button is disabled or not.
   *
   * @returns {boolean}
   */
  _isDisabled() {
    return this.props._disabled;
  }

  /**
   * Handles clicking / pressing the button, and toggles the audio mute state
   * accordingly.
   *
   * @override
   * @protected
   * @returns {void}
   */
  _handleClick() {
    this._setAudioMuted(!this._isAudioMuted());
  }

  /**
   * Indicates whether this button is in toggled state or not.
   *
   * @override
   * @protected
   * @returns {boolean}
   */
  _isToggled() {
    return this._isAudioMuted();
  }
}

/**
 * Function that maps parts of Redux state tree into component props.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
  const _audioMuted = isLocalTrackMuted(
    state['features/base/tracks'],
    MEDIA_TYPE.AUDIO
  );
  const _conference = state['features/base/conference'].conference;
  const _disabled = isAudioMuteButtonDisabled(state);
  const enabledFlag = getFeatureFlag(state, AUDIO_MUTE_BUTTON_ENABLED, true);

  return {
    isOpen: getAudioSettingsVisibility(state),
    _audioMuted,
    _isVideoOnly: state['features/settings'].videoOnly,
    _disabled,
    _conference,
    _userIdleMode: state['features/settings'].userIdleMode,
    visible: enabledFlag
  };
}
JoinAudioButton.propTypes = {
  buttonKey: PropTypes.string,
  isDesktop: PropTypes.bool,
  notifyMode: PropTypes.bool,
  isOpen: PropTypes.bool,
  isDisabled: PropTypes.bool,
  hasPermissions: PropTypes.any
};
export default React.memo(
  translate(connect(mapStateToProps)(withBoundary(JoinAudioButton)))
);
